(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (!root) return;

  root.PoolsetCartUpsell = api;

  const productCache = new Map();
  const configCache = new Map();

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function formatMoney(cents, currency, compact = false) {
    // compact: whole-dollar amounts drop the decimals (A$149), others keep them (A$149.50).
    const options = { style: 'currency', currency: currency || 'AUD' };
    if (compact && cents % 100 === 0) options.maximumFractionDigits = 0;
    return new Intl.NumberFormat(undefined, options).format(cents / 100);
  }

  function productUrl(handle) {
    const rootPath = root.Shopify?.routes?.root || '/';
    return `${rootPath.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}`;
  }

  async function loadConfig(url) {
    if (!configCache.has(url)) {
      configCache.set(
        url,
        api.fetchJson(url).then((config) => {
          const validation = api.validateConfig(config);
          if (!validation.valid) throw new Error(validation.error);
          return config;
        })
      );
    }
    return configCache.get(url);
  }

  async function loadProduct(addOn, force = false) {
    const cacheKey = `${addOn.productId}:${addOn.variantId}:${addOn.handle}`;
    if (force) productCache.delete(cacheKey);
    if (!productCache.has(cacheKey)) {
      productCache.set(
        cacheKey,
        api.fetchJson(`${productUrl(addOn.handle)}.js`).then((product) => {
          const variant = product.variants?.find((candidate) => api.toPositiveInteger(candidate.id) === addOn.variantId);
          if (api.toPositiveInteger(product.id) !== addOn.productId || !variant) {
            throw new Error(`Configured add-on ${addOn.key} no longer matches its product or variant.`);
          }
          return {
            ...addOn,
            available: variant.available === true,
            image: variant.featured_image?.src || product.featured_image || product.images?.[0] || '',
            price: api.toNonNegativeInteger(variant.price),
            title: product.title || addOn.title,
            url: productUrl(addOn.handle),
          };
        })
      );
    }
    return productCache.get(cacheKey);
  }

  async function loadPlanProducts(plan, force = false) {
    const products = new Map();
    const requirements = plan.mappingPlans.flatMap((mappingPlan) => mappingPlan.requirements).filter((requirement) => requirement.missing > 0);
    await Promise.all(
      requirements.map(async (requirement) => {
        try {
          products.set(requirement.addOn.productId, await loadProduct(requirement.addOn, force));
        } catch (error) {
          products.set(requirement.addOn.productId, { ...requirement.addOn, available: false, error: error.message });
        }
      })
    );
    return products;
  }

  function appendProductItem(list, product, quantity, currency, recommended) {
    const item = createElement('li', 'transformer-upsell__item');
    if (product.image) {
      const image = createElement('img', 'transformer-upsell__image');
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 56;
      image.height = 56;
      item.append(image);
    } else {
      item.append(createElement('span', 'transformer-upsell__image transformer-upsell__image--empty'));
    }
    const link = createElement('a', 'reversed-link', product.title);
    link.href = product.url;
    if (recommended) link.dataset.recommended = '';
    item.append(link);
    const price = product.price >= 0 ? ` × ${formatMoney(product.price, currency)}` : '';
    item.append(createElement('span', 'transformer-upsell__quantity', `${quantity}${price}`));
    list.append(item);
  }

  // One authoritative commit path for every surface (cart drawer, cart page, product page).
  // cart/update.js with absolute per-variant targets is idempotent: repeating it cannot add more.
  // Errors carry `ambiguous` once the request may have been committed, so callers never retry blindly.
  async function commitRecommendations(config, plan, cart, surface) {
    const products = await loadPlanProducts(plan, true);
    const unavailable = api.findUnavailableRequirement(plan, products);
    if (unavailable) throw new Error(`${unavailable.addOn.title} is currently unavailable.`);
    const updates = api.buildAbsoluteUpdates(cart.items, plan);
    if (!updates.valid) throw new Error(updates.error);
    const sections = api.collectSections();
    const payload = api.buildMutationPayload(cart, plan, updates.values, sections, root.location.pathname);
    let requestStarted = false;
    let mutationConfirmed = false;
    try {
      requestStarted = true;
      const response = await fetch(api.route('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const updatedState = await response.json().catch(() => ({}));
      if (!response.ok || updatedState.status) {
        requestStarted = false;
        throw new Error(updatedState.description || updatedState.message || `Request failed (${response.status}).`);
      }
      mutationConfirmed = true;

      const authoritativeCart = await api.fetchJson(api.route('cart.js'));
      const confirmed = api.calculatePlan(authoritativeCart.items, config, surface);
      if (!confirmed.valid || api.hasMissing(confirmed)) {
        throw new Error('Shopify did not confirm the expected add-on quantities. Reload the page before trying again.');
      }
      const discountCodes = api.collectDiscountCodes(cart, plan).filter((code) => !api.cartHasApplicableDiscount(authoritativeCart, code));
      if (discountCodes.length > 0) {
        throw new Error('The add-ons were added, but Shopify did not confirm the configured discount. Reload the cart before trying again.');
      }

      authoritativeCart.sections = updatedState.sections || {};
      if (!sections.every((sectionId) => typeof authoritativeCart.sections[sectionId] === 'string')) {
        throw new Error('The add-ons were added, but the cart view could not be refreshed. Reload the page before trying again.');
      }
      return { authoritativeCart, updatedState };
    } catch (error) {
      error.ambiguous = requestStarted || mutationConfirmed;
      throw error;
    }
  }

  // The product-page opt-in needs the theme's AJAX cart drawer (pubsub) to refresh it; otherwise it stays hidden
  // and the guide falls back to pointing customers at the cart prompt.
  function canUseProductOptIn() {
    return Boolean(root.FoxTheme?.pubsub?.publish && root.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate && root.FoxTheme?.settings?.cartType !== 'page');
  }

  class CartUpsellFramework extends HTMLElement {
    async connectedCallback() {
      document.addEventListener('input', this.lockForPendingCartEdit, true);
      document.addEventListener('change', this.lockForPendingCartEdit, true);
      document.addEventListener('click', this.lockForPendingCartRemoval, true);
      await this.render();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleAdd);
      document.removeEventListener('input', this.lockForPendingCartEdit, true);
      document.removeEventListener('change', this.lockForPendingCartEdit, true);
      document.removeEventListener('click', this.lockForPendingCartRemoval, true);
    }

    lockForPendingCartEdit = (event) => {
      if (event.target.matches('cart-items [name="updates[]"]')) this.lockAll('Update the cart before adding accessories.');
    };

    lockForPendingCartRemoval = (event) => {
      if (event.target.closest('cart-items [is="cart-remove-item"]')) this.lockAll('Waiting for the cart to update…');
    };

    async render() {
      this.hidden = true;
      this.replaceChildren();
      try {
        this.config = await loadConfig(this.dataset.configUrl);
        const cart = await api.fetchJson(api.route('cart.js'));
        const plan = api.calculatePlan(cart.items, this.config, this.dataset.surface);
        if (!plan.valid) throw new Error(plan.error);
        if (!api.hasMissing(plan)) {
          return;
        }

        const products = await loadPlanProducts(plan);
        if (this.dataset.surface === 'drawer') {
          this.renderCompact(plan, products);
          return;
        }
        const unavailable = [];
        let totalMissingUnits = 0;
        const container = createElement('div', 'cart-upsell-framework grid gap-4');

        for (const mappingPlan of plan.mappingPlans) {
          const missing = mappingPlan.requirements.filter((requirement) => requirement.missing > 0);
          if (missing.length === 0) continue;
          const card = createElement('section', 'transformer-upsell grid gap-3');
          const headingRow = createElement('div', 'transformer-upsell__heading-row');
          const missingUnits = api.countMissingUnits(missing);
          totalMissingUnits += missingUnits;
          const copyValues = { quantity: mappingPlan.triggerQuantity, trigger: mappingPlan.mapping.copy.triggerNoun, count: missingUnits };
          headingRow.append(createElement('h3', 'transformer-upsell__heading h5', api.formatCopy(api.pickCopy(mappingPlan.mapping.copy, 'cartHeading', missingUnits), copyValues)));
          if (mappingPlan.mapping.testOnly) headingRow.append(createElement('span', 'transformer-upsell__test-badge', 'TEST ONLY'));
          card.append(headingRow);
          card.append(createElement('p', 'transformer-upsell__copy text-sm text-subtext', api.formatCopy(api.pickCopy(mappingPlan.mapping.copy, 'cartBody', missingUnits), copyValues)));
          const list = createElement('ul', 'transformer-upsell__items');
          list.role = 'list';
          for (const requirement of missing) {
            const product = products.get(requirement.addOn.productId);
            if (!product?.available) unavailable.push(product?.error || `${requirement.addOn.title} is unavailable.`);
            appendProductItem(list, product || requirement.addOn, requirement.missing, this.config.currency, true);
          }
          card.append(list);
          if (mappingPlan.mapping.discount?.enabled) {
            card.append(createElement('p', 'transformer-upsell__discount text-sm', mappingPlan.mapping.discount.customerText));
          }
          container.append(card);
        }

        // The label must live in .btn__text: the theme's hover fill (.btn::before) paints over bare text nodes.
        this.button = createElement('button', 'btn btn--secondary w-full');
        this.button.append(createElement('span', 'btn__text', api.buttonLabel(this.config, totalMissingUnits)));
        this.button.type = 'button';
        this.button.dataset.cartUpsellAdd = '';
        this.status = createElement('p', 'transformer-upsell__status text-sm');
        this.status.dataset.cartUpsellStatus = '';
        this.status.role = 'status';
        this.status.setAttribute('aria-live', 'polite');
        this.button.addEventListener('click', this.handleAdd);
        container.append(this.button, this.status);
        this.append(container);
        this.displayedSignature = plan.signature;
        if (unavailable.length > 0) this.lockAll(unavailable[0]);
        this.hidden = false;
      } catch {
        this.replaceChildren();
        this.hidden = true;
      }
    }

    // Drawer variant: one compact row (thumbnail, one-line text + price, slim Add button, short caption). The cart
    // page keeps the full card above because it has the room.
    renderCompact(plan, products) {
      const summary = api.compactSummary(plan, products, this.config, (cents) => formatMoney(cents, this.config.currency, true));
      const unavailable = [];
      for (const mappingPlan of plan.mappingPlans) {
        for (const requirement of mappingPlan.requirements.filter((candidate) => candidate.missing > 0)) {
          const product = products.get(requirement.addOn.productId);
          if (!product?.available) unavailable.push(product?.error || `${requirement.addOn.title} is unavailable.`);
        }
      }
      const container = createElement('div', 'cart-upsell-framework');
      const card = createElement('section', 'transformer-upsell transformer-upsell--compact');
      const row = createElement('div', 'transformer-upsell__compact-row');
      if (summary.image) {
        const image = createElement('img', 'transformer-upsell__thumb');
        image.src = summary.image;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 40;
        image.height = 40;
        row.append(image);
      } else {
        row.append(createElement('span', 'transformer-upsell__thumb transformer-upsell__image--empty'));
      }
      const text = createElement('div', 'transformer-upsell__compact-text');
      const title = createElement('p', 'transformer-upsell__compact-title', summary.text);
      if (summary.testOnly) title.append(' ', createElement('span', 'transformer-upsell__test-badge', 'TEST ONLY'));
      text.append(title);
      if (summary.caption) text.append(createElement('p', 'transformer-upsell__compact-caption', summary.caption));
      row.append(text);

      // The label must live in .btn__text: the theme's hover fill (.btn::before) paints over bare text nodes.
      this.button = createElement('button', 'btn btn--secondary btn--small transformer-upsell__add');
      this.button.append(createElement('span', 'btn__text', summary.buttonText));
      this.button.type = 'button';
      this.button.setAttribute('aria-label', summary.buttonLabel);
      this.button.dataset.cartUpsellAdd = '';
      this.button.addEventListener('click', this.handleAdd);
      row.append(this.button);

      card.append(row);

      this.status = createElement('p', 'transformer-upsell__status text-sm');
      this.status.dataset.cartUpsellStatus = '';
      this.status.role = 'status';
      this.status.setAttribute('aria-live', 'polite');
      card.append(this.status);
      container.append(card);
      this.append(container);
      this.displayedSignature = plan.signature;
      if (unavailable.length > 0) this.lockAll(unavailable[0]);
      this.hidden = false;
    }

    handleAdd = async () => {
      if (!this.button || this.button.disabled || this.adding) return;
      this.adding = true;
      this.lockAll('Checking your cart…', true);
      let mutationConfirmed = false;

      try {
        const cart = await api.fetchJson(api.route('cart.js'));
        const plan = api.calculatePlan(cart.items, this.config, this.dataset.surface);
        if (!plan.valid) throw new Error(plan.error);
        if (plan.signature !== this.displayedSignature) {
          throw new Error('Your cart changed in another view. Reload the page to see the current recommendations.');
        }
        if (!api.hasMissing(plan)) {
          this.setStatus('Your cart already contains the recommended accessories.');
          return;
        }

        const { authoritativeCart, updatedState } = await commitRecommendations(this.config, plan, cart, this.dataset.surface);
        mutationConfirmed = true;

        if (root.FoxTheme?.pubsub?.publish && root.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
          root.FoxTheme.pubsub.publish(root.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: authoritativeCart });
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: updatedState } }));
          this.setStatus('Recommended accessories added.');
        } else {
          root.location.reload();
        }
      } catch (error) {
        const ambiguous = error.ambiguous === true || mutationConfirmed;
        this.setStatus(
          ambiguous
            ? 'The cart may have changed. Reload the page before trying again.'
            : error.message || 'The accessories could not be added. Please try again.'
        );
        if (!ambiguous && !String(error.message).includes('Reload')) this.unlockAll();
      } finally {
        document.querySelectorAll('[data-cart-upsell-add]').forEach((button) => button.classList.remove('btn--loading'));
        this.adding = false;
      }
    };

    lockAll(message, loading = false) {
      document.querySelectorAll('[data-cart-upsell-add]').forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        if (loading) button.classList.add('btn--loading');
      });
      this.setStatus(message);
    }

    unlockAll() {
      document.querySelectorAll('[data-cart-upsell-add]').forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      });
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  class ProductUpsellGuide extends HTMLElement {
    checked = false;
    busy = false;
    locked = false;
    rerun = false;
    statusMessage = '';
    settled = false;
    cartItems = null;

    async connectedCallback() {
      this.quantityInput = this.closest('product-info')?.querySelector('[name="quantity"]');
      this.quantityInput?.addEventListener('input', this.onQuantity);
      this.quantityInput?.addEventListener('change', this.onQuantity);
      // The theme's product form (main button and sticky bar alike) announces a successful add with this event.
      document.addEventListener('product-ajax:added', this.handleProductAdded);
      try {
        this.config = await loadConfig(this.dataset.configUrl);
        this.mappings = this.config.mappings.filter(
          (mapping) => mapping.enabled && mapping.surfaces.pdp && mapping.triggerProductIds.includes(api.toPositiveInteger(this.dataset.productId))
        );
        const addOns = this.mappings.flatMap((mapping) => Object.values(mapping.addOns));
        this.products = new Map();
        await Promise.all(
          addOns.map(async (addOn) => {
            try {
              this.products.set(addOn.productId, await loadProduct(addOn));
            } catch (error) {
              this.products.set(addOn.productId, { ...addOn, available: false, error: error.message });
            }
          })
        );
        await this.refreshCart();
        this.update();
      } catch {
        this.replaceChildren();
      }
    }

    disconnectedCallback() {
      this.quantityInput?.removeEventListener('input', this.onQuantity);
      this.quantityInput?.removeEventListener('change', this.onQuantity);
      document.removeEventListener('product-ajax:added', this.handleProductAdded);
    }

    // The cart only informs the on-page recommendation. The add itself always re-reads the authoritative cart.
    async refreshCart() {
      try {
        const cart = await api.fetchJson(api.route('cart.js'));
        this.cartItems = Array.isArray(cart.items) ? cart.items : null;
      } catch {
        this.cartItems = null;
      }
    }

    onQuantity = () => {
      this.statusMessage = '';
      this.settled = false;
      this.update();
    };

    handleProductAdded = (event) => {
      if (!this.mappings) return;
      const addedProductId = api.toPositiveInteger(event.detail?.product?.product_id);
      if (!addedProductId || addedProductId !== api.toPositiveInteger(this.dataset.productId)) return;
      // Once the light is in the cart, the guide describes the cart as it now stands, until the quantity changes again.
      this.settled = true;
      if (this.checked) {
        this.addRecommended();
      } else {
        this.refreshCart().then(this.update);
      }
    };

    // Runs after the theme has added the light(s). Idempotent: the plan is recalculated from the authoritative
    // cart and committed as absolute targets, so repeats, double clicks and existing transformers cannot duplicate lines.
    async addRecommended() {
      if (this.busy) {
        this.rerun = true;
        return;
      }
      this.busy = true;
      try {
        do {
          this.rerun = false;
          await this.addRecommendedOnce();
        } while (this.rerun && !this.locked);
      } finally {
        this.busy = false;
        this.update();
      }
    }

    async addRecommendedOnce() {
      this.statusMessage = this.copyFor('pdpAddingText', this.units || 1, 'Adding the recommended transformer…');
      this.update();
      document.querySelectorAll('[data-cart-upsell-add]').forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      });
      let addedUnits = this.units || 1;
      try {
        const cart = await api.fetchJson(api.route('cart.js'));
        const plan = api.calculatePlan(cart.items, this.config, 'pdp');
        if (!plan.valid) throw new Error(plan.error);
        addedUnits = api.countMissingUnits(plan.mappingPlans.flatMap((mappingPlan) => mappingPlan.requirements)) || addedUnits;
        if (api.hasMissing(plan)) {
          const { authoritativeCart } = await commitRecommendations(this.config, plan, cart, 'pdp');
          this.cartItems = authoritativeCart.items;
          root.FoxTheme.pubsub.publish(root.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: authoritativeCart });
        } else {
          this.cartItems = cart.items;
        }
        this.checked = false;
        this.statusMessage = this.copyFor('pdpAddedText', addedUnits, 'The recommended transformer is in your cart.');
      } catch (error) {
        // The light(s) are already in the cart. Never retry after a possibly committed write.
        this.locked = error.ambiguous === true;
        await this.refreshCart();
        this.statusMessage = this.locked
          ? this.copyFor('pdpAmbiguousText', addedUnits, 'Your cart may have changed. Please check your cart before adding the transformer.')
          : this.copyFor('pdpFailedText', addedUnits, 'Your light was added, but the transformer could not be. You can add it from your cart.');
      }
    }

    copyFor(field, count, fallback) {
      const copy = this.mappings?.[0]?.copy || {};
      return api.formatCopy(api.pickCopy(copy, field, count) || fallback, { count });
    }

    // Per-mapping missing requirements for the chosen quantity plus whatever is already in the cart.
    displayPlans(quantity) {
      const productId = api.toPositiveInteger(this.dataset.productId);
      let plan = null;
      if (this.cartItems) {
        plan = this.settled ? api.calculatePlan(this.cartItems, this.config, 'pdp') : api.calculateProductPagePlan(this.cartItems, this.config, productId, quantity);
      }
      const result = [];
      for (const mapping of this.mappings) {
        let requirements;
        let covered = false;
        const mappingPlan = plan?.valid ? plan.mappingPlans.find((candidate) => candidate.mapping.id === mapping.id) : null;
        if (mappingPlan) {
          requirements = mappingPlan.requirements.filter((requirement) => requirement.missing > 0).map((requirement) => ({ addOn: requirement.addOn, required: requirement.missing }));
          covered = requirements.length === 0;
        } else {
          requirements = api.calculateRequirements(quantity, mapping);
        }
        if (!requirements) continue;
        result.push({ mapping, requirements, covered });
      }
      return result;
    }

    update = () => {
      if (!this.mappings) return;
      const quantity = api.toPositiveInteger(this.quantityInput?.value || 1) || 1;
      this.replaceChildren();
      const optInEnabled = canUseProductOptIn() && !this.locked;
      let totalUnits = 0;
      let anyUnavailable = false;
      let firstCopy = null;
      let unavailableText = '';

      for (const { mapping, requirements, covered } of this.displayPlans(quantity)) {
        const units = api.countMissingUnits(requirements.map((requirement) => ({ missing: requirement.required })));
        totalUnits += units;
        firstCopy = firstCopy || mapping.copy;
        const card = createElement('section', 'transformer-guide');
        const headingRow = createElement('div', 'transformer-upsell__heading-row');
        headingRow.append(createElement('h3', 'transformer-guide__heading h5', api.pickCopy(mapping.copy, 'pdpHeading', covered ? 1 : units)));
        if (mapping.testOnly) headingRow.append(createElement('span', 'transformer-upsell__test-badge', 'TEST ONLY'));
        card.append(headingRow);
        if (covered) {
          card.append(createElement('p', 'transformer-guide__message', api.pickCopy(mapping.copy, 'pdpCoveredText', 1) || 'The recommended transformer setup is already in your cart.'));
          this.append(card);
          continue;
        }
        const summary = requirements
          .map((requirement) => `${requirement.required} × ${this.products.get(requirement.addOn.productId)?.title || requirement.addOn.title}`)
          .join(' plus ');
        card.append(
          createElement(
            'p',
            'transformer-guide__message',
            api.formatCopy(mapping.copy.pdpBody, { quantity, requirements: summary, trigger: mapping.copy.triggerNoun })
          )
        );
        // Recommendation cards are informational only: no link to the transformer's own page.
        const list = createElement('ul', 'transformer-guide__links');
        list.role = 'list';
        for (const requirement of requirements) {
          const product = this.products.get(requirement.addOn.productId) || requirement.addOn;
          const item = createElement('li', 'transformer-guide__item');
          const cardBody = createElement('div', 'transformer-guide__card');
          cardBody.dataset.recommended = '';
          if (product.image) {
            const image = createElement('img', 'transformer-guide__image');
            image.src = product.image;
            image.alt = '';
            image.loading = 'lazy';
            image.width = 48;
            image.height = 48;
            cardBody.append(image);
          }
          cardBody.append(createElement('span', 'transformer-guide__card-title', `${requirement.required} × ${product.title}`));
          item.append(cardBody);
          if (product.available === false) {
            anyUnavailable = true;
            unavailableText = mapping.availability.unavailableText;
            item.append(createElement('span', 'text-sm', unavailableText));
          }
          list.append(item);
        }
        card.append(list);
        if (mapping.copy.pdpNote) card.append(createElement('p', 'transformer-guide__note text-sm text-subtext', mapping.copy.pdpNote));
        this.append(card);
      }

      this.units = totalUnits;
      if (totalUnits > 0 && firstCopy) {
        const footer = createElement('div', 'transformer-guide__optin-wrap');
        if (optInEnabled && !anyUnavailable) {
          const label = createElement('label', 'transformer-guide__optin');
          const input = createElement('input');
          input.type = 'checkbox';
          input.checked = this.checked;
          input.disabled = this.busy;
          input.dataset.upsellPdpCheckbox = '';
          input.addEventListener('change', () => {
            this.checked = input.checked;
          });
          label.append(input, createElement('span', 'transformer-guide__optin-text', api.formatCopy(api.pickCopy(firstCopy, 'pdpCheckboxLabel', totalUnits), { count: totalUnits })));
          footer.append(label);
        }
        const hint = api.pickCopy(firstCopy, optInEnabled ? 'pdpCartHint' : 'pdpCartOnlyHint', totalUnits);
        if (hint) footer.append(createElement('p', 'transformer-guide__hint text-sm text-subtext', api.formatCopy(hint, { count: totalUnits })));
        this.append(footer);
      }

      const status = createElement('p', 'transformer-guide__status text-sm');
      status.dataset.upsellPdpStatus = '';
      status.role = 'status';
      status.setAttribute('aria-live', 'polite');
      status.textContent = this.statusMessage;
      this.append(status);
    };
  }

  if (!customElements.get('cart-upsell-framework')) customElements.define('cart-upsell-framework', CartUpsellFramework);
  if (!customElements.get('product-upsell-guide')) customElements.define('product-upsell-guide', ProductUpsellGuide);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  function toPositiveInteger(value) {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : 0;
  }

  function toNonNegativeInteger(value) {
    const number = Number(value);
    return Number.isInteger(number) && number >= 0 ? number : -1;
  }

  function formatCopy(template, values) {
    return String(template || '').replace(/\{([a-zA-Z]+)\}/g, (match, key) => (values[key] === undefined ? match : String(values[key])));
  }

  // Picks `<base>One` / `<base>Many` copy by quantity, falling back to `<base>`.
  function pickCopy(copy, base, count) {
    const source = copy || {};
    const specific = count === 1 ? source[`${base}One`] : count > 1 ? source[`${base}Many`] : undefined;
    const value = typeof specific === 'string' && specific ? specific : source[base];
    return typeof value === 'string' ? value : '';
  }

  function countMissingUnits(requirements) {
    return (requirements || []).reduce((sum, requirement) => sum + (toPositiveInteger(requirement.missing) || 0), 0);
  }

  function buttonLabel(config, units) {
    const template = units === 1 ? config.cartButtonTextOne : units > 1 ? config.cartButtonTextMany : '';
    return formatCopy(typeof template === 'string' && template ? template : config.cartButtonText, { count: units });
  }

  // The product-page recommendation: the light quantity being chosen PLUS everything already in the cart,
  // run through the same calculatePlan the cart surfaces use (one source of truth).
  function calculateProductPagePlan(cartItems, config, productId, quantity) {
    const pending = { key: '__pending-product-page-line__', product_id: toPositiveInteger(productId), variant_id: 1, quantity: toPositiveInteger(quantity) || 1 };
    return calculatePlan([...(Array.isArray(cartItems) ? cartItems : []), pending], config, 'pdp');
  }

  // Everything the compact drawer row says, from config copy + live product data. Pure, so it is unit-tested.
  function compactSummary(plan, products, config, formatPrice) {
    const parts = [];
    let units = 0;
    let cents = 0;
    let priced = true;
    let image = '';
    let triggerQuantity = 0;
    let copy = null;
    let testOnly = false;
    for (const mappingPlan of plan.mappingPlans) {
      const missing = mappingPlan.requirements.filter((requirement) => requirement.missing > 0);
      if (missing.length === 0) continue;
      copy = copy || mappingPlan.mapping.copy || {};
      testOnly = testOnly || mappingPlan.mapping.testOnly === true;
      triggerQuantity += mappingPlan.triggerQuantity;
      for (const requirement of missing) {
        const addOn = requirement.addOn;
        const product = products.get(addOn.productId) || addOn;
        const short = typeof addOn.shortLabel === 'string' && addOn.shortLabel ? addOn.shortLabel : product.title || addOn.title;
        const shortMany = typeof addOn.shortLabelMany === 'string' && addOn.shortLabelMany ? addOn.shortLabelMany : short;
        parts.push(requirement.missing > 1 ? `${requirement.missing} × ${shortMany}` : short);
        units += requirement.missing;
        if (product.price >= 0) cents += product.price * requirement.missing;
        else priced = false;
        if (!image && product.image) image = product.image;
      }
    }
    copy = copy || {};
    const price = priced && typeof formatPrice === 'function' ? formatPrice(cents) : '';
    let template = pickCopy(copy, 'cartCompactText', triggerQuantity) || 'Add {items} for your lights · {price}';
    if (!price) template = template.replace(/\s*[·|–—-]\s*\{price\}/, '');
    const values = { items: parts.join(' + '), price, count: units, quantity: triggerQuantity, trigger: copy.triggerNoun || '' };
    return {
      text: formatCopy(template, values).trim(),
      caption: formatCopy(pickCopy(copy, 'cartCompactCaption', units), values),
      buttonText: copy.cartCompactButton || 'Add',
      buttonLabel: buttonLabel(config, units),
      units,
      cents,
      image,
      testOnly,
    };
  }

  function validateConfig(config) {
    if (!config || config.version !== 1 || !Array.isArray(config.mappings) || config.mappings.length === 0) {
      return { valid: false, error: 'Cart upsell configuration is missing or unsupported.' };
    }
    const ids = new Set();
    for (const mapping of config.mappings) {
      if (!mapping || typeof mapping.id !== 'string' || !mapping.id || ids.has(mapping.id)) {
        return { valid: false, error: 'Each cart upsell mapping needs a unique id.' };
      }
      ids.add(mapping.id);
      if (!Array.isArray(mapping.triggerProductIds) || mapping.triggerProductIds.length === 0 || mapping.triggerProductIds.some((id) => !toPositiveInteger(id))) {
        return { valid: false, error: `Mapping ${mapping.id} has invalid trigger products.` };
      }
      if (
        !Array.isArray(mapping.triggerProductHandles) ||
        mapping.triggerProductHandles.length !== mapping.triggerProductIds.length ||
        mapping.triggerProductHandles.some((handle) => typeof handle !== 'string' || !handle)
      ) {
        return { valid: false, error: `Mapping ${mapping.id} has invalid trigger product metadata.` };
      }
      if (
        !mapping.surfaces ||
        !['pdp', 'cartDrawer', 'cartPage'].every((surface) => typeof mapping.surfaces[surface] === 'boolean') ||
        !mapping.copy ||
        !['triggerNoun', 'cartHeading', 'cartBody', 'pdpHeading', 'pdpBody'].every(
          (field) => typeof mapping.copy[field] === 'string' && mapping.copy[field]
        )
      ) {
        return { valid: false, error: `Mapping ${mapping.id} has invalid rendering metadata.` };
      }
      if (!mapping.addOns || typeof mapping.addOns !== 'object' || Object.keys(mapping.addOns).length === 0) {
        return { valid: false, error: `Mapping ${mapping.id} has no add-ons.` };
      }
      for (const [key, addOn] of Object.entries(mapping.addOns)) {
        if (
          addOn.key !== key ||
          !toPositiveInteger(addOn.productId) ||
          !toPositiveInteger(addOn.variantId) ||
          typeof addOn.handle !== 'string' ||
          !addOn.handle ||
          typeof addOn.title !== 'string' ||
          !addOn.title
        ) {
          return { valid: false, error: `Mapping ${mapping.id} has an invalid add-on ${key}.` };
        }
      }
      if (!mapping.quantityRule || !['pair_and_odd', 'one_if_present', 'one_per_trigger'].includes(mapping.quantityRule.type)) {
        return { valid: false, error: `Mapping ${mapping.id} has an unsupported quantity rule.` };
      }
      if (mapping.availability?.mode !== 'live_variant') {
        return { valid: false, error: `Mapping ${mapping.id} must use live variant availability.` };
      }
      if (mapping.discount?.enabled && (!mapping.discount.cartOnly || !String(mapping.discount.code || '').trim())) {
        return { valid: false, error: `Mapping ${mapping.id} has an invalid cart-only discount configuration.` };
      }
      if (!calculateRequirements(1, mapping)) {
        return { valid: false, error: `Mapping ${mapping.id} quantity rule references an unknown add-on.` };
      }
    }
    return { valid: true };
  }

  function calculateRequirements(triggerQuantity, mapping) {
    const quantity = toPositiveInteger(triggerQuantity);
    if (!quantity) return [];
    const rule = mapping.quantityRule;
    let values;
    if (rule.type === 'pair_and_odd') {
      values = [
        { key: rule.pairAddOnKey, required: Math.floor(quantity / 2) },
        { key: rule.oddAddOnKey, required: quantity % 2 },
      ];
    } else if (rule.type === 'one_if_present') {
      values = [{ key: rule.addOnKey, required: toPositiveInteger(rule.quantity) || 1 }];
    } else if (rule.type === 'one_per_trigger') {
      const units = toPositiveInteger(rule.triggerUnitsPerAddOn) || 1;
      values = [{ key: rule.addOnKey, required: Math.ceil(quantity / units) }];
    } else {
      return null;
    }
    const requirements = [];
    for (const value of values) {
      if (!mapping.addOns[value.key]) return null;
      if (value.required > 0) requirements.push({ addOn: mapping.addOns[value.key], required: value.required });
    }
    return requirements;
  }

  function normalizeCartItems(items) {
    if (!Array.isArray(items)) return { valid: false, error: 'The cart response is invalid.' };
    const keys = new Set();
    const normalized = [];
    for (const item of items) {
      const quantity = toPositiveInteger(item.quantity);
      const productId = toPositiveInteger(item.product_id || item.productId);
      const variantId = toPositiveInteger(item.variant_id || item.variantId || item.id);
      const key = typeof item.key === 'string' ? item.key : '';
      if (!quantity || !productId || !variantId || !key || keys.has(key)) {
        return { valid: false, error: 'The cart contains an ambiguous line and cannot be changed safely.' };
      }
      keys.add(key);
      normalized.push({ ...item, productId, variantId, quantity, key });
    }
    return { valid: true, items: normalized };
  }

  function surfaceEnabled(mapping, surface) {
    if (!mapping.surfaces) return false;
    if (surface === 'drawer') return mapping.surfaces.cartDrawer === true;
    if (surface === 'cart-page') return mapping.surfaces.cartPage === true;
    return true;
  }

  function calculatePlan(items, config, surface) {
    const validation = validateConfig(config);
    if (!validation.valid) return { valid: false, error: validation.error, mappingPlans: [] };
    const cart = normalizeCartItems(items);
    if (!cart.valid) return { valid: false, error: cart.error, mappingPlans: [] };
    const rawPlans = [];

    for (const mapping of config.mappings) {
      if (!mapping.enabled || !surfaceEnabled(mapping, surface)) continue;
      const triggerQuantity = cart.items
        .filter((item) => mapping.triggerProductIds.includes(item.productId))
        .reduce((sum, item) => sum + item.quantity, 0);
      if (!triggerQuantity) continue;
      const requirements = calculateRequirements(triggerQuantity, mapping);
      if (!requirements) return { valid: false, error: `Mapping ${mapping.id} could not be calculated.`, mappingPlans: [] };
      rawPlans.push({ mapping, triggerQuantity, requirements });
    }

    const grouped = new Map();
    for (const mappingPlan of rawPlans) {
      for (const requirement of mappingPlan.requirements) {
        const current = grouped.get(requirement.addOn.productId);
        if (current && current.addOn.variantId !== requirement.addOn.variantId) {
          return { valid: false, error: `Add-on ${requirement.addOn.productId} has conflicting variants.`, mappingPlans: [] };
        }
        if (!current) grouped.set(requirement.addOn.productId, { addOn: requirement.addOn, required: 0, entries: [] });
        const group = grouped.get(requirement.addOn.productId);
        group.required += requirement.required;
        group.entries.push(requirement);
      }
    }

    for (const [productId, group] of grouped) {
      let availablePresent = cart.items.filter((item) => item.productId === productId).reduce((sum, item) => sum + item.quantity, 0);
      for (const requirement of group.entries) {
        const covered = Math.min(requirement.required, availablePresent);
        requirement.present = covered;
        requirement.missing = requirement.required - covered;
        availablePresent -= covered;
      }
      group.present = group.required - group.entries.reduce((sum, entry) => sum + entry.missing, 0);
      group.missing = Math.max(group.required - group.present, 0);
    }

    // An app may add an unrelated gift between rendering and the customer's click.
    // Revalidate every configured trigger/add-on, but do not reject a safe add-on for that gift alone.
    const relevantProductIds = new Set(
      config.mappings
        .filter((mapping) => mapping.enabled && surfaceEnabled(mapping, surface))
        .flatMap((mapping) => [...mapping.triggerProductIds, ...Object.values(mapping.addOns).map((addOn) => addOn.productId)])
    );
    const cartFingerprint = cart.items
      .filter((item) => relevantProductIds.has(item.productId))
      .map((item) => `${item.key}:${item.productId}:${item.variantId}:${item.quantity}`)
      .sort();
    const recommendationFingerprint = rawPlans.map((mappingPlan) => ({
      id: mappingPlan.mapping.id,
      triggerQuantity: mappingPlan.triggerQuantity,
      missing: mappingPlan.requirements.map((requirement) => [requirement.addOn.productId, requirement.missing]),
    }));
    return {
      valid: true,
      cartItems: cart.items,
      mappingPlans: rawPlans,
      groups: [...grouped.values()],
      signature: JSON.stringify({ cart: cartFingerprint, recommendations: recommendationFingerprint }),
    };
  }

  function hasMissing(plan) {
    return plan.mappingPlans.some((mappingPlan) => mappingPlan.requirements.some((requirement) => requirement.missing > 0));
  }

  function findUnavailableRequirement(plan, products) {
    return plan.mappingPlans
      .flatMap((mappingPlan) => mappingPlan.requirements)
      .filter((requirement) => requirement.missing > 0)
      .find((requirement) => products.get(requirement.addOn.productId)?.available !== true);
  }

  function isPlainLine(item) {
    const properties = item.properties || {};
    return !item.selling_plan_allocation && Object.keys(properties).length === 0;
  }

  function buildAbsoluteUpdates(items, plan) {
    if (!plan?.valid) return { valid: false, values: {}, error: 'The cart could not be verified.' };
    const cart = normalizeCartItems(items);
    if (!cart.valid) return { valid: false, values: {}, error: cart.error };
    const values = {};
    for (const group of plan.groups) {
      if (group.missing === 0) continue;
      const lines = cart.items.filter((item) => item.productId === group.addOn.productId);
      if (lines.length === 0) {
        values[String(group.addOn.variantId)] = group.missing;
        continue;
      }
      const line = lines.find(isPlainLine);
      if (!line) {
        return {
          valid: false,
          values: {},
          error: `An existing ${group.addOn.title} line has custom options. Update it manually before using this recommendation.`,
        };
      }
      values[line.key] = line.quantity + group.missing;
    }
    return { valid: true, values };
  }

  function collectDiscountCodes(cart, plan) {
    const codes = [];
    for (const discount of cart?.discount_codes || []) {
      if (discount?.applicable !== false && String(discount?.code || '').trim()) codes.push(String(discount.code).trim());
    }
    for (const mappingPlan of plan.mappingPlans) {
      if (
        mappingPlan.mapping.discount?.enabled &&
        mappingPlan.mapping.discount.cartOnly &&
        mappingPlan.requirements.some((requirement) => requirement.missing > 0)
      ) {
        codes.push(String(mappingPlan.mapping.discount.code).trim());
      }
    }
    return [...new Set(codes)];
  }

  function cartHasApplicableDiscount(cart, code) {
    return (cart?.discount_codes || []).some((discount) => discount?.applicable === true && discount.code === code);
  }

  function buildMutationPayload(cart, plan, updates, sections, sectionsUrl) {
    const payload = { updates, sections, sections_url: sectionsUrl };
    const discounts = collectDiscountCodes(cart, plan);
    if (discounts.length > 0) payload.discount = discounts.join(',');
    return payload;
  }

  function route(path) {
    const rootPath = typeof window !== 'undefined' ? window.Shopify?.routes?.root || '/' : '/';
    return `${rootPath.replace(/\/$/, '')}/${String(path).replace(/^\//, '')}`;
  }

  async function fetchJson(url, options) {
    const response = await fetch(url, options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) throw new Error(data.description || data.message || `Request failed (${response.status}).`);
    return data;
  }

  function collectSections() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
    return [...new Set(sections.filter(Boolean))];
  }

  return {
    buildAbsoluteUpdates,
    buildMutationPayload,
    buttonLabel,
    calculatePlan,
    calculateProductPagePlan,
    calculateRequirements,
    cartHasApplicableDiscount,
    collectDiscountCodes,
    collectSections,
    compactSummary,
    countMissingUnits,
    pickCopy,
    fetchJson,
    findUnavailableRequirement,
    formatCopy,
    hasMissing,
    normalizeCartItems,
    route,
    toNonNegativeInteger,
    toPositiveInteger,
    validateConfig,
  };
});